import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@/utils/classnames';

import {
  TooltipBase,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';
import { Typography, type TypographyVariant } from './typography';

interface TruncatedTextProps {
  variant: TypographyVariant;
  className?: string;
  children: React.ReactNode;
  /**
   * HTML element to render. Pass `as="span"` when inside a `<button>` —
   * a `<p>` inside a `<button>` is invalid HTML and breaks the tooltip.
   */
  as?: React.ElementType;
}

/**
 * Renders text truncated with `text-overflow: ellipsis`. When the content
 * overflows its container a tooltip is shown automatically so the full text
 * is still accessible. The tooltip is suppressed when nothing is clipped.
 *
 * Drop-in replacement for `<Typography className="truncate">`.
 */
export function TruncatedText({
  variant,
  className,
  children,
  as,
}: TruncatedTextProps) {
  const ref = useRef<HTMLElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [tooltipText, setTooltipText] = useState('');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const check = () => {
      const truncated = el.scrollWidth > el.offsetWidth;
      setIsTruncated(truncated);
      if (truncated) {
        setTooltipText(el.textContent ?? '');
      }
    };

    check();

    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [children]);

  return (
    <TooltipProvider delayDuration={300}>
      <TooltipBase>
        <TooltipTrigger asChild>
          <Typography
            ref={ref}
            variant={variant}
            className={cn('block truncate', className)}
            as={as}
          >
            {children}
          </Typography>
        </TooltipTrigger>
        {isTruncated && (
          <TooltipContent className="w-auto max-w-xs p-2 text-xs">
            {tooltipText}
          </TooltipContent>
        )}
      </TooltipBase>
    </TooltipProvider>
  );
}
